import React from 'react';
import PropTypes from 'prop-types';
import { Link } from '@tempmee/react';
import history from '../../history';

/**
 * A modified click — cmd/ctrl/shift/alt, or the middle button — is the browser's
 * "open this somewhere else" gesture. It has to reach the anchor's real `href`
 * untouched; calling `preventDefault` on it turns an intended new tab into a
 * same-tab navigation, which is the bug this guard exists to avoid.
 */
export const isModifiedClick = (event) =>
  event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1;

/**
 * An in-app link that stays a link.
 *
 * `href` is real, so the browser offers everything it normally offers for one:
 * the right-click context menu's "Open link in new tab", cmd/ctrl/middle-click,
 * and the URL on hover. Only a plain left click is intercepted and turned back
 * into a `history.push`, so ordinary navigation stays a SPA transition rather
 * than a full document reload.
 *
 * Safe to drop inside a `react-data-table-component` cell: that library fires
 * `onRowClicked` only when the click target itself carries
 * `data-tag="allowRowEvents"`, which this anchor does not, so clicking here
 * does not also run the row's own handler.
 *
 * The href comes from `history.createHref` rather than from `to` directly. The
 * app is not always served from the domain root — preview builds sit under
 * `/admin-portlal/pr-<n>` — and `history.push` applies that basename while a
 * raw href does not, being resolved against the origin instead. Asking the same
 * history object for the href keeps the two in agreement wherever the app is
 * mounted.
 */
export const RouteLink = ({ to, children, ...anchorProps }) => (
  <Link
    {...anchorProps}
    href={history.createHref({ pathname: to })}
    onClick={(event) => {
      if (isModifiedClick(event)) {
        return;
      }
      event.preventDefault();
      history.push(to);
    }}
  >
    {children}
  </Link>
);

RouteLink.propTypes = {
  /** App route to navigate to, e.g. `/user/dentist/123`. */
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
